import { EventEmitter, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { switchMap } from 'rxjs/operators';
import { NewsIndisposService } from '../../shared/providers/news-indispos/news-indispos.service';
import {
  CrtToastCloseType,
  CrtToastController,
  CrtToastEventResponse,
  CrtToastOption,
  CrtTypeCommToast,
} from './toast.service';

/**
 * Type de notification
 */
export enum CrtNotificationType {
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
  INFO = 'info',
}

/**
 * Type de notification
 */
export enum CrtNotificationSousType {
  COMMUNICATION = 'communication',
  NEWS = 'news',
  INDISPOS = 'indispos',
  REFRESH_NI = 'refresh_ni',
}

/**
 * Interception des notifications CRT
 */
@Injectable({ providedIn: 'root' })
export class CrtNotificationHandler {
  /**
   * Durée d'un toast d'une news et indispo
   */
  private readonly newsToastDuration = 10000;

  /**
   * Class CSS pour toast news, pour l'icône
   */
  private readonly cssClassToastNews = 'crt-toast-news';

  /**
   * Class CSS pour toast indispos, pour l'icône
   */
  private readonly cssClassToastIndispos = 'crt-toast-indispos';

  /**
   * Constructeur
   * @param {CrtToastController} crtToastCtrl
   * @param newsIndispoService
   */
  constructor(
    private crtToastCtrl: CrtToastController,
    private newsIndispoService: NewsIndisposService
  ) {}

  /**
   * Gère les notifications CRT
   * @param notification
   * @returns
   */
  public handleNotifications(notification: NotificationCRT): Observable<CrtToastEventResponse> {
    switch (notification.sousType) {
      case CrtNotificationSousType.INDISPOS:
      case CrtNotificationSousType.REFRESH_NI:
      case CrtNotificationSousType.NEWS:
        return this.newsIndispoService.getNewsIndispos(true, false).pipe(
          switchMap((newsIndispo) => {
            if (newsIndispo.some((elmt) => elmt.identifiant === parseInt(notification.idNewsIndispo))) {
              return this.switchBackgroundForegroundReceive(notification);
            } else {
              return of();
            }
          })
        );
      case CrtNotificationSousType.COMMUNICATION:
      default:
        return this.switchBackgroundForegroundReceive(notification);
    }
  }

  private switchBackgroundForegroundReceive(notification: NotificationCRT): Observable<CrtToastEventResponse> {
    if (notification.tap) {
      return this.handleBackgroundReceiveClick(notification);
    } else {
      return this.openNotificationToast(notification);
    }
  }

  private handleBackgroundReceiveClick(notification: NotificationCRT): Observable<CrtToastEventResponse> {
    return of({ data: { ...notification }, type: CrtToastCloseType.ACTION });
  }

  /**
   * Ouvre un Toast Crt
   * @param notification
   * @returns {Observable<CrtToastEventResponse>}
   */
  private openNotificationToast(notification: NotificationCRT): Observable<CrtToastEventResponse> {
    if (!(notification && notification.body)) {
      return of();
    }

    let toast: Promise<EventEmitter<CrtToastEventResponse>>;

    let duration = 0;
    let cssClass = '';

    if (notification.sousType === CrtNotificationSousType.NEWS) {
      duration = this.newsToastDuration;
      cssClass = this.cssClassToastNews;
    } else if (notification.sousType === CrtNotificationSousType.INDISPOS) {
      cssClass = this.cssClassToastIndispos;
    }

    const toastOpts: CrtToastOption = {
      message: notification.body,
      data: { ...notification },
      action: true,
      isTimed: notification.sousType === CrtNotificationSousType.NEWS,
      duration: duration,
      cssClass: cssClass,
    };

    switch (notification.type) {
      case CrtNotificationType.WARNING:
        toast = this.crtToastCtrl.createWarning(toastOpts);
        break;
      case CrtNotificationType.ERROR:
        toast = this.crtToastCtrl.createDanger(toastOpts);
        break;
      case CrtNotificationType.SUCCESS:
        toast = this.crtToastCtrl.createSuccess(toastOpts);
        break;
      case CrtNotificationType.INFO:
        toast = this.crtToastCtrl.createInfo(toastOpts);
        break;
      default:
        return throwError(() => new Error('Notification type not implemented'));
    }

    return fromPromise(toast).pipe(switchMap((evt) => evt));
  }
}

export interface NotificationCRT {
  body: string;
  title: string;
  type: CrtNotificationType;
  sousType?: CrtNotificationSousType;
  tap?: boolean;
  idNewsIndispo?: string;
  idMetier?: number;
  idSite?: number;
  typeComm?: CrtTypeCommToast;
}
